/**
 * AdCampaignMapping (/crm/settings/ad-campaigns) — which study each Meta ad
 * campaign was selling. Plan: docs/crm-ad-spend-roi-plan.md §7.6 (semantics §2.2).
 *
 * It lives in CRM Settings rather than Platform Admin because it is CRM
 * configuration and its data is in `crm.*`.
 *
 * THE ONE THING THIS SCREEN GETS RIGHT THAT A PLAIN DROPDOWN WOULD NOT.
 * A mapping changes for two opposite reasons, and guessing between them
 * silently rewrites months of attribution:
 *   - "Fix it — it was always this study" should rewrite history (the mapping
 *     was simply wrong before);
 *   - "Changed as of <date>" must NOT (the campaign was repurposed; June's
 *     spend belongs to June's study).
 * So saving asks which one you mean instead of inferring it from whether a date
 * was supplied. That is why there are two buttons and not one.
 *
 * Unmapped campaigns sort first: a campaign nobody has answered for is the only
 * thing on this page that needs anyone's attention.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Link2, Loader2, Megaphone } from 'lucide-react';
import api from '../../../services/api';
import PageHeader, { RefreshButton } from '../../components/PageHeader';
import Modal from '../../components/forms/Modal';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../components/filters/styles';
import { Alert, Badge } from '../../components/ui';
import { fmtCurrency } from '../../components/reports/StatBlocks';
import { StudyOption, studyInternalLabel } from '../../types';

interface StudyShare {
  study_id: string | null;
  study_slug: string | null;
  study_title: string | null;
  leads: number;
  share: number | null;
}

interface CampaignRow {
  campaign_id: string;
  campaign_name: string | null;
  spend: number | null;
  currency: string | null;
  leads: number;
  studies: StudyShare[];
  /** present only on rows that already have a mapping */
  id?: string;
  study_id?: string | null;
  effective_from?: string;
  effective_to?: string | null;
  source?: 'auto' | 'manual';
  lead_share?: number | null;
  mapped: boolean;
}

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function AdCampaignMapping() {
  const [rows, setRows] = useState<CampaignRow[]>([]);
  const [studies, setStudies] = useState<StudyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [editing, setEditing] = useState<CampaignRow | null>(null);
  const [pick, setPick] = useState<string>('');
  const [changedOn, setChangedOn] = useState<string>(todayISO());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get('/api/crm/analytics/ad-spend/campaign-studies')
      .then((res) => {
        const mapped: CampaignRow[] = (res.data?.mappings || [])
          // Only the currently-open period is editable here; closed periods are
          // history and the ledger keeps them.
          .filter((m: CampaignRow) => m.effective_to === null || m.effective_to === undefined)
          .map((m: CampaignRow) => ({ ...m, mapped: true }));
        const unmapped: CampaignRow[] = (res.data?.unmapped_campaigns || [])
          .map((u: CampaignRow) => ({ ...u, mapped: false }));
        // Unmapped first — they are the only rows that need a human.
        setRows([...unmapped, ...mapped]);
        setDisabled(false);
      })
      .catch((err) => {
        if (err?.response?.data?.code === 'AD_ROI_DISABLED') { setDisabled(true); return; }
        setError(err?.response?.data?.error || 'Failed to load campaign mappings');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    api.get('/api/crm/studies')
      .then((res) => {
        if (!alive) return;
        setStudies((res.data?.studies || []).filter((s: StudyOption) => s.id && s.source === 'crm'));
      })
      .catch(() => { /* the select still allows keeping the current value */ });
    return () => { alive = false; };
  }, []);

  const openEditor = (row: CampaignRow) => {
    setEditing(row);
    setPick(row.study_id || '');
    setChangedOn(todayISO());
    setSaveError(null);
  };

  const save = async (mode: 'correct' | 'change') => {
    if (!editing) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.put(
        `/api/crm/analytics/ad-spend/campaign-studies/${encodeURIComponent(editing.campaign_id)}`,
        {
          study_id: pick || null,
          mode,
          effective_from: mode === 'change' ? changedOn : null,
        },
      );
      setNotice(
        mode === 'correct'
          ? 'Mapping corrected — every period for this campaign now reads the new study.'
          : `Mapping changed as of ${changedOn} — earlier spend keeps its previous study.`,
      );
      setEditing(null);
      load();
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || 'Could not save the mapping');
    } finally {
      setSaving(false);
    }
  };

  const unmappedCount = useMemo(() => rows.filter((r) => !r.mapped).length, [rows]);

  if (disabled) {
    return (
      <div className="space-y-4">
        <PageHeader icon={Megaphone} title="Ad campaigns" subtitle="Which study each Meta campaign was selling" />
        <Alert tone="info">
          Ad spend reporting is switched off. Set <code>CRM_AD_ROI_ENABLED=true</code> on the
          API service to use this page.
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        icon={Megaphone}
        title="Ad campaigns"
        count={rows.length}
        subtitle="Which study each Meta campaign was selling. Spend is never split across studies — Meta charges the campaign."
        actions={<RefreshButton onClick={load} loading={loading} />}
      />

      {notice && <Alert tone="success" onDismiss={() => setNotice(null)}>{notice}</Alert>}
      {error && <Alert tone="error">{error}</Alert>}

      {!loading && unmappedCount > 0 && (
        <Alert tone="warning">
          {unmappedCount} campaign{unmappedCount === 1 ? '' : 's'} with leads {unmappedCount === 1 ? 'has' : 'have'} no
          study yet. Their spend and outcomes still count in the all-campaigns total — they
          just cannot be attributed to a study.
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
          <Megaphone className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
          <p className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">No campaigns yet</p>
          <p className="mx-auto max-w-sm text-sm text-gray-500 dark:text-gray-400">
            A campaign appears here once one of its ads has produced a lead, or once the
            nightly Meta sync has run.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Campaign</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Spend</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Leads</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Where its leads went</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Mapped to</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map((r) => {
                  const mix = r.studies || [];
                  const multi = mix.length > 1;
                  return (
                    <tr key={r.campaign_id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        <div className="font-medium">{r.campaign_name || r.campaign_id}</div>
                        {r.campaign_name && (
                          <div className="text-xs tabular-nums text-gray-500 dark:text-gray-400">{r.campaign_id}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900 dark:text-gray-100">
                        {r.spend === null || r.spend === undefined
                          ? <span className="text-gray-400 dark:text-gray-500" title="No spend has synced for this campaign yet">—</span>
                          : fmtCurrency(Number(r.spend), r.currency)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900 dark:text-gray-100">
                        {Number(r.leads || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {mix.length === 0 ? (
                          <span className="text-gray-400 dark:text-gray-500">no leads yet</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5">
                            {multi && (
                              <AlertTriangle
                                className="h-3.5 w-3.5 flex-shrink-0 text-amber-500"
                                aria-label="This campaign's leads went to more than one study"
                              />
                            )}
                            <span className={multi ? 'text-amber-700 dark:text-amber-400' : ''}>
                              {mix.slice(0, 3).map((m) => (
                                `${m.study_slug || 'unknown'} ${m.share == null ? '' : `${Math.round(m.share * 100)}%`}`
                              )).join(' · ')}
                            </span>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {r.mapped ? (
                          <span className="inline-flex items-center gap-1.5">
                            {r.study_id
                              ? (studies.find((s) => s.id === r.study_id)
                                ? studyInternalLabel(studies.find((s) => s.id === r.study_id)!)
                                : r.study_id)
                              : <span className="text-gray-500 dark:text-gray-400">deliberately unmapped</span>}
                            <Badge tone={r.source === 'manual' ? 'primary' : 'gray'}>{r.source}</Badge>
                          </span>
                        ) : (
                          <span className="text-amber-700 dark:text-amber-400">not mapped</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button type="button" className={secondaryBtnCls} onClick={() => openEditor(r)}>
                          <Link2 className="h-3.5 w-3.5" />
                          {r.mapped ? 'Change' : 'Map it'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <Modal
          isOpen
          onClose={() => (saving ? undefined : setEditing(null))}
          title={editing.campaign_name || editing.campaign_id}
          maxWidth="max-w-lg"
        >
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300" htmlFor="ad-map-study">
                Study this campaign was selling
              </label>
              <select
                id="ad-map-study"
                className={`${fieldCls} w-full`}
                value={pick}
                onChange={(e) => setPick(e.target.value)}
              >
                <option value="">— leave deliberately unmapped —</option>
                {studies.map((s) => (
                  <option key={s.id} value={s.id || ''}>{studyInternalLabel(s)}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Choosing “leave deliberately unmapped” records that you answered the
                question, so the automatic mapper stops suggesting this campaign.
              </p>
            </div>

            {editing.studies?.length > 1 && (
              <Alert tone="warning">
                This campaign’s leads went to {editing.studies.length} different studies
                ({editing.studies.map((m) => `${m.study_slug} ${m.share == null ? '' : `${Math.round(m.share * 100)}%`}`).join(', ')}).
                A campaign maps to one study per period, and its spend is never split —
                so pick the study it was actually selling.
              </Alert>
            )}

            {saveError && <Alert tone="error">{saveError}</Alert>}

            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <p className="mb-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                Which of these is it?
              </p>
              <div className="space-y-3">
                <div>
                  <button
                    type="button"
                    className={`${primaryBtnCls} w-full justify-center`}
                    disabled={saving}
                    onClick={() => save('correct')}
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Fix — it was always this study
                  </button>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    The mapping was simply wrong. Every period for this campaign is
                    re-pointed, so past reports change too.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      className={fieldCls}
                      value={changedOn}
                      onChange={(e) => setChangedOn(e.target.value)}
                      aria-label="Date the campaign changed study"
                    />
                    <button
                      type="button"
                      className={`${secondaryBtnCls} flex-1 justify-center`}
                      disabled={saving || !changedOn}
                      onClick={() => save('change')}
                    >
                      Changed as of this date
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    The campaign was repurposed. Spend before this date keeps the study it
                    already had — history is not rewritten.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
